import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import {
  ArrowPathIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  CalendarIcon,
  DocumentTextIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import DateQuickPresets from '../../components/DateQuickPresets';

const DATASET_DEFAULT = {
  resumo: {
    saldo_anterior: 0,
    debitos: 0,
    creditos: 0,
    saldo_final: 0,
    impacto_caixa: 0,
    movimentos: 0,
  },
  contas: [],
  detalhes: [],
};

const MovimentacaoPatrimonial = () => {
  const hoje = new Date();
  const [carregando, setCarregando] = useState(false);
  const [planos, setPlanos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [dados, setDados] = useState(DATASET_DEFAULT);
  const [filtros, setFiltros] = useState({
    data_inicio: format(new Date(hoje.getFullYear(), hoje.getMonth(), 1), 'yyyy-MM-dd'),
    data_fim: format(hoje, 'yyyy-MM-dd'),
    plano_conta_id: 'todos',
    conta_bancaria_id: 'todas',
  });

  const formatarValor = (value) => Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const carregarFiltros = async () => {
    try {
      const [planosResponse, contasResponse] = await Promise.all([
        axios.get('/api/plano-contas'),
        axios.get('/api/contas-bancarias'),
      ]);
      setPlanos((Array.isArray(planosResponse.data) ? planosResponse.data : []).filter((plano) => plano.tipo === 'patrimonio'));
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      toast.error('Erro ao carregar filtros patrimoniais');
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/patrimonial-movimentacao', { params: filtros });
      const payload = response.data || {};
      setDados({
        resumo: { ...DATASET_DEFAULT.resumo, ...(payload.resumo || {}) },
        contas: Array.isArray(payload.contas) ? payload.contas : [],
        detalhes: Array.isArray(payload.detalhes) ? payload.detalhes : [],
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar movimentacao patrimonial');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const detalhesPorConta = useMemo(() => {
    const map = new Map();
    dados.detalhes.forEach((item) => {
      const key = item.plano_conta_id || 'sem-conta';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return map;
  }, [dados.detalhes]);

  const cards = [
    { label: 'Saldo Anterior', value: dados.resumo.saldo_anterior, icon: ScaleIcon, color: 'text-gray-700', bg: 'bg-gray-100' },
    { label: 'Debitos Patrimoniais', value: dados.resumo.debitos, icon: DocumentTextIcon, color: 'text-danger-700', bg: 'bg-danger-100' },
    { label: 'Creditos Patrimoniais', value: dados.resumo.creditos, icon: DocumentTextIcon, color: 'text-success-700', bg: 'bg-success-100' },
    { label: 'Saldo Liquido', value: dados.resumo.saldo_final, icon: BuildingLibraryIcon, color: Number(dados.resumo.saldo_final || 0) >= 0 ? 'text-success-700' : 'text-danger-700', bg: 'bg-primary-100' },
    { label: 'Impacto no Caixa', value: dados.resumo.impacto_caixa, icon: BanknotesIcon, color: Number(dados.resumo.impacto_caixa || 0) >= 0 ? 'text-success-700' : 'text-danger-700', bg: 'bg-blue-100' },
  ];

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Movimentacao Patrimonial</h1>
          <p className="text-gray-600">Razao gerencial das contas patrimoniais, incluindo lancamentos compostos.</p>
        </div>
        <button type="button" onClick={carregarDados} className="btn-secondary self-start lg:self-end">
          <ArrowPathIcon className="h-5 w-5 mr-2" />
          Atualizar
        </button>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <DateQuickPresets
            mode="range"
            startDate={filtros.data_inicio}
            endDate={filtros.data_fim}
            onSelect={({ startDate, endDate }) => setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))}
            className="mb-4"
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <div>
              <label className="form-label">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Data Inicio
              </label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros((old) => ({ ...old, data_inicio: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">
                <CalendarIcon className="h-4 w-4 inline mr-1" />
                Data Fim
              </label>
              <input
                type="date"
                className="form-input"
                value={filtros.data_fim}
                onChange={(e) => setFiltros((old) => ({ ...old, data_fim: e.target.value }))}
              />
            </div>
            <div>
              <label className="form-label">Conta Patrimonial</label>
              <select
                className="form-select"
                value={filtros.plano_conta_id}
                onChange={(e) => setFiltros((old) => ({ ...old, plano_conta_id: e.target.value }))}
              >
                <option value="todos">Todas</option>
                {planos.map((plano) => (
                  <option key={plano.id} value={plano.id}>
                    {plano.codigo} - {plano.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select
                className="form-select"
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}
              >
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                Saida de banco gera debito patrimonial; entrada de banco gera credito patrimonial.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className={`rounded-full p-3 ${card.bg}`}>
                    <Icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                  <div className="ml-4 min-w-0">
                    <p className="text-sm text-gray-500">{card.label}</p>
                    <p className={`text-xl font-bold ${card.color}`}>R$ {formatarValor(card.value)}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="card mb-6">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-semibold">Resumo por Conta Patrimonial</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Conta</th>
                  <th className="text-right">Saldo Anterior</th>
                  <th className="text-right">Debitos</th>
                  <th className="text-right">Creditos</th>
                  <th className="text-right">Saldo Liquido</th>
                  <th className="text-right">Movimentos</th>
                </tr>
              </thead>
              <tbody>
                {dados.contas.map((item) => (
                  <tr key={item.plano_conta_id}>
                    <td>
                      <p className="font-medium text-gray-900">{item.plano_codigo} - {item.plano_descricao}</p>
                      <p className="text-xs text-gray-500">{(detalhesPorConta.get(item.plano_conta_id) || []).length} detalhe(s) no periodo</p>
                    </td>
                    <td className="text-right">R$ {formatarValor(item.saldo_anterior)}</td>
                    <td className="text-right text-danger-700">R$ {formatarValor(item.debitos)}</td>
                    <td className="text-right text-success-700">R$ {formatarValor(item.creditos)}</td>
                    <td className={`text-right font-semibold ${Number(item.saldo_final || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
                      R$ {formatarValor(item.saldo_final)}
                    </td>
                    <td className="text-right">{item.movimentos}</td>
                  </tr>
                ))}
                {dados.contas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      {carregando ? 'Carregando...' : 'Nenhuma movimentacao patrimonial encontrada.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Lancamentos Patrimoniais</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descricao</th>
                  <th>Conta Patrimonial</th>
                  <th>Banco</th>
                  <th>Origem</th>
                  <th className="text-right">Debito</th>
                  <th className="text-right">Credito</th>
                  <th className="text-right">Caixa</th>
                </tr>
              </thead>
              <tbody>
                {dados.detalhes.map((item) => (
                  <tr key={`${item.id}-${item.plano_conta_id}-${item.data}-${item.valor}`}>
                    <td>{item.data}</td>
                    <td>
                      <p className="font-medium text-gray-900">{item.descricao}</p>
                      <p className="text-xs text-gray-500">{item.direcao_caixa === 'entrada' ? 'Entrada no caixa' : 'Saida do caixa'}</p>
                    </td>
                    <td>{item.plano_codigo} - {item.plano_descricao}</td>
                    <td>{[item.conta_banco, item.conta_numero].filter(Boolean).join(' - ') || '-'}</td>
                    <td>
                      <span className="badge badge-secondary">{item.tipo === 'composto' ? 'Composto' : 'Patrimonial'}</span>
                    </td>
                    <td className="text-right text-danger-700">R$ {formatarValor(item.debito_patrimonial)}</td>
                    <td className="text-right text-success-700">R$ {formatarValor(item.credito_patrimonial)}</td>
                    <td className={`text-right font-medium ${Number(item.impacto_caixa || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
                      R$ {formatarValor(item.impacto_caixa)}
                    </td>
                  </tr>
                ))}
                {dados.detalhes.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500">
                      {carregando ? 'Carregando...' : 'Nenhum lancamento no periodo.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovimentacaoPatrimonial;
